import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, tap, switchMap, retry, throttleTime } from 'rxjs/operators';
import { of, throwError, timer } from 'rxjs';
import {
  createPdfExport,
  createPdfExportFailed,
  createPdfExportSubmitted,
  createPdfExportSuccess,
} from './pdf-export.action';
import { PdfExportClientService } from '@mode/report-viewer/pdf-export/data-access';
import { PdfExportDomain } from '@mode/shared/contract-common';

const STOP_STATES = [PdfExportDomain.PdfExportState.Completed, PdfExportDomain.PdfExportState.Failed];
const RETRY_MSG = 'Loading';

@Injectable()
export class PdfExportEffects {
  createExportPdf$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createPdfExport),
      // Limits to 1 request to prevent accidental over calling
      throttleTime(1000),
      // switch map because we don't currently support exporting more than one report at a time
      switchMap((action) => {
        return this.pdfExportClient.postPdfExport(action.reportRunToken).pipe(
          map((pdfExport) => {
            switch (pdfExport.state) {
              case PdfExportDomain.PdfExportState.Completed:
                return createPdfExportSuccess({ pdfExport: pdfExport });
              case PdfExportDomain.PdfExportState.Failed:
                return createPdfExportFailed({ state: pdfExport.state, error: pdfExport.error, showDialog: true });
              default:
                // If it isn't completed or failed, assume a job is running
                return createPdfExportSubmitted({
                  reportRunToken: action.reportRunToken,
                  showDialog: action.showDialog,
                });
            }
          })
        );
      })
    );
  });

  pollExportPdf$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(createPdfExportSubmitted),
      // switch map because we don't currently support exporting more than one report at a time
      switchMap((action) => {
        /**
         * Polling works by throwing a certain error if the PDF state is
         * not complete. The retryWhen operator will restart the source
         * observable until either some other error is thrown, or the state
         * is complete.
         */
        return of(null).pipe(
          concatMap(() => this.pdfExportClient.getPdfExport(action.reportRunToken, true)),
          tap((pdfExport) => {
            if (!STOP_STATES.includes(pdfExport.state)) {
              throw new Error(RETRY_MSG);
            }
          }),
          retry({
            delay: (err, retryCount) => {
              if (err?.message !== RETRY_MSG) {
                return throwError(() => err);
              } else {
                return timer(Math.min(2500, (Math.pow(2, retryCount) - 1) * 100));
              }
            },
          })
        );
      }),
      map((pdfExport) => {
        switch (pdfExport.state) {
          case PdfExportDomain.PdfExportState.Completed:
            return createPdfExportSuccess({ pdfExport: pdfExport });
          case PdfExportDomain.PdfExportState.Failed:
            return createPdfExportFailed({ state: pdfExport.state, error: pdfExport.error });
          default:
            // Shouldn't ever reach this normally
            return createPdfExportFailed({ state: pdfExport.state, error: new Error('Internal polling failure') });
        }
      }),
      catchError((err) => of(createPdfExportFailed({ error: err })))
    );
  });

  constructor(private actions$: Actions, private pdfExportClient: PdfExportClientService) {}
}
